import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Edit, Plus } from 'lucide-react';

const EditCircleDialog = ({
  open, onOpenChange, data, setData, onSubmit,
  newCategory, setNewCategory, onAddCategory, onRemoveCategory,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogTrigger asChild>
      <Button variant="outline" size="sm" data-testid="edit-circle-btn">
        <Edit className="w-4 h-4 mr-2" /> Edit
      </Button>
    </DialogTrigger>
    <DialogContent className="surface-card max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Edit Circle</DialogTitle>
        <DialogDescription>Update circle details and support links</DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="edit-circle-name">Circle Name</Label>
          <Input id="edit-circle-name" data-testid="edit-circle-name-input" value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} required />
        </div>
        <div className="space-y-2">
          <Label htmlFor="edit-circle-description">Description</Label>
          <Textarea id="edit-circle-description" data-testid="edit-circle-description-input" value={data.description} onChange={(e) => setData({ ...data, description: e.target.value })} className="resize-none" />
        </div>

        <div className="border-t border-border pt-4 mt-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Support & Resource Links (Optional)</h3>
          <div className="space-y-3">
            {[
              { id: 'venmo-username', label: 'Venmo Username', field: 'venmo_username', placeholder: 'username (without @)' },
              { id: 'cashapp-username', label: 'Cash App Username', field: 'cashapp_username', placeholder: 'username (without $)' },
              { id: 'zelle-info', label: 'Zelle Email or Phone', field: 'zelle_info', placeholder: 'email@example.com or phone number' },
              { id: 'gofundme-link', label: 'GoFundMe Campaign Link', field: 'gofundme_link', placeholder: 'https://gofundme.com/...' },
              { id: 'caringbridge-link', label: 'CaringBridge Page Link', field: 'caringbridge_link', placeholder: 'https://caringbridge.org/...' },
            ].map(({ id, label, field, placeholder }) => (
              <div key={id} className="space-y-2">
                <Label htmlFor={id} className="text-xs">{label}</Label>
                <Input id={id} placeholder={placeholder} value={data[field] || ''} onChange={(e) => setData({ ...data, [field]: e.target.value })} />
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-border pt-4 mt-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Resource Library Categories</h3>
          <p className="text-xs text-muted-foreground mb-3">Customize the tabs in your Resource Library. Leave empty to use defaults.</p>
          {data.resource_categories && data.resource_categories.length > 0 && (
            <div className="space-y-2 mb-3">
              {data.resource_categories.map((cat, index) => (
                <div key={cat.key || cat.label} className="flex items-center justify-between bg-muted/50 p-2 rounded">
                  <span className="text-sm">{cat.label}</span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => onRemoveCategory(index)} className="text-destructive hover:text-destructive">Remove</Button>
                </div>
              ))}
            </div>
          )}
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="cat-label" className="text-xs">Display Name</Label>
                <Input id="cat-label" placeholder="e.g., Middle Circle Tips" value={newCategory.label} onChange={(e) => setNewCategory({ ...newCategory, label: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="cat-value" className="text-xs">Category ID</Label>
                <Input id="cat-value" placeholder="e.g., middle_tips" value={newCategory.value} onChange={(e) => setNewCategory({ ...newCategory, value: e.target.value.toLowerCase().replace(/\s+/g, '_') })} />
              </div>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={onAddCategory} className="w-full">
              <Plus className="w-3 h-3 mr-1" /> Add Category
            </Button>
          </div>
        </div>

        <Button type="submit" className="w-full btn-primary" data-testid="submit-edit-circle-btn">Save Changes</Button>
      </form>
    </DialogContent>
  </Dialog>
);

export default EditCircleDialog;
